import type { ClientOrganization } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { ModalProps } from 'react-native';

import { OrganizationPreviewModal } from '@/features/profile-setting/components/organization-edit/OrganizationPreviewModal';
import { TerritorialAuthorityPreviewModal } from '@/features/profile-setting/components/organization-edit/TerritorialAuthorityPreviewModal';
import type { OrganizationEditFormValues } from '@/features/profile-setting/components/organization-edit/types';

type Props = Omit<ModalProps, 'children'> & {
  organization: ClientOrganization;
  variant: 'organization' | 'territorialAuthority';
};

/**
 * Form-bound wrapper around the preview modals: reads the freshly-picked
 * logo itself so the parent form doesn't need to watch `logoResult`. When
 * the user picked a new local image, the persisted logo is hidden so the
 * preview shows the pick instead.
 */
export const EditPreviewModal: FC<Props> = ({
  organization,
  variant,
  ...modalProps
}) => {
  const { control } = useFormContext<OrganizationEditFormValues>();
  const logoResult = useWatch({ control, name: 'logoResult' });

  const currentLogoImage = logoResult?.localImage
    ? undefined
    : organization.logoImage;

  if (variant === 'territorialAuthority') {
    return (
      <TerritorialAuthorityPreviewModal
        {...modalProps}
        organization={organization}
        currentLogoImage={currentLogoImage}
      />
    );
  }

  return (
    <OrganizationPreviewModal
      {...modalProps}
      organization={organization}
      currentLogoImage={currentLogoImage}
    />
  );
};
